"use client";

import React, { useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { TagInput } from "@/components/ui/tags/TagInput";
import { CommonTagChips } from "@/components/ui/tags/CommonTagChips";
import { EmailAliasesEditor } from "@/features/contacts/components/EmailAliasesEditor";
import { PhoneAliasesEditor } from "@/features/contacts/components/PhoneAliasesEditor";
import { TechnicalIdentifiersEditor } from "@/features/companies/components/TechnicalIdentifiersEditor";
import { parseTechnicalIdentifiers } from "@wtc/technical-identifiers";
import { TicketSelectWithModal } from "@/components/ui/selects/components/TicketSelectWithModal";
import { ReusableTemplateSelector } from "@/components/shared/ReusableTemplateSelector";
import { extractTemplateFormValues } from "@/lib/utils/crm/template-helpers";
import { FieldWrapper } from "./context";
import { cn } from "@/lib/utils";
import type { FieldRenderContext } from "./context";
import type { BaseTemplate } from "../../UniversalEntityPage/types";

/**
 * Render a tags field.
 */
export const TagsFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const tagsConfig = field.tagsConfig || {};
  const currentTagsRaw = watchedValues[fieldName];

  // Ensure we always have an array, handle various input types
  let currentTags: string[] = [];
  if (Array.isArray(currentTagsRaw)) {
    currentTags = currentTagsRaw.filter((tag) => tag && typeof tag === "string");
  } else if (typeof currentTagsRaw === "string" && currentTagsRaw.trim()) {
    currentTags = [currentTagsRaw.trim()];
  } else if (currentTagsRaw) {
    currentTags = [String(currentTagsRaw)];
  }

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please add at least one tag"}
    >
      {/* Common Tag Chips */}
      {tagsConfig.commonTags && (
        <CommonTagChips
          currentTags={currentTags}
          onTagsChange={(tags) => setValue(fieldName, tags)}
          commonTags={tagsConfig.commonTags}
          columns={4}
        />
      )}

      {/* Custom Tag Input */}
      {tagsConfig.allowCustom !== false && (
        <TagInput
          value={currentTags}
          onChange={(tags) => setValue(fieldName, tags)}
          placeholder={field.placeholder || "Add custom tags..."}
        />
      )}
    </FieldWrapper>
  );
};

/**
 * Render an array field.
 */
export const ArrayFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const arrayConfig = field.arrayConfig || {};
  const currentArrayRaw = watchedValues[fieldName];

  // Ensure we always have an array, handle various input types
  let currentArray: string[] = [];
  if (Array.isArray(currentArrayRaw)) {
    currentArray = currentArrayRaw.filter((item) => item && typeof item === "string");
  } else if (typeof currentArrayRaw === "string" && currentArrayRaw.trim()) {
    currentArray = [currentArrayRaw.trim()];
  } else if (currentArrayRaw) {
    currentArray = [String(currentArrayRaw)];
  }

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please add at least one item"}
    >
      {/* Common Items Chips */}
      {arrayConfig.commonItems && arrayConfig.commonItems.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {arrayConfig.commonItems.map((item) => (
            <Button
              key={item}
              type="button"
              variant={currentArray.includes(item) ? "default" : "outline"}
              size="sm"
              onClick={() => {
                const updated = currentArray.includes(item)
                  ? currentArray.filter((i) => i !== item)
                  : [...currentArray, item];
                setValue(fieldName, updated, { shouldValidate: true });
              }}
              className="text-xs h-7 px-2"
            >
              {item}
            </Button>
          ))}
        </div>
      )}

      {/* Custom Array Input */}
      {arrayConfig.allowCustom !== false && (
        <TagInput
          value={currentArray}
          onChange={(items) => setValue(fieldName, items, { shouldValidate: true })}
          placeholder={field.placeholder || "Add items (press Enter to add)..."}
        />
      )}

      {/* Display current items as badges */}
      {currentArray.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {currentArray.map((item, index) => (
            <Badge key={`${item}-${index}`} variant="secondary" className="flex items-center gap-1">
              {item}
              <button
                type="button"
                onClick={() => {
                  const updated = currentArray.filter((_, i) => i !== index);
                  setValue(fieldName, updated, { shouldValidate: true });
                }}
                className="ml-1 hover:text-red-600"
                aria-label={`Remove ${item}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </FieldWrapper>
  );
};

/**
 * Render an email_aliases field.
 */
export const EmailAliasesFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const currentValue = watchedValues[fieldName];

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please add at least one email alias"}
    >
      <EmailAliasesEditor
        contact={
          {
            id: "temp-id",
            email: "",
            email_aliases: Array.isArray(currentValue) ? currentValue : [],
          }
        }
        onUpdate={(updatedContact) => {
          setValue(fieldName, updatedContact.email_aliases || [], {
            shouldValidate: true,
          });
        }}
        showEditorDirectly={true}
        readOnlyMode={true}
      />
    </FieldWrapper>
  );
};

/**
 * Render a phone_aliases field.
 */
export const PhoneAliasesFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const currentValue = watchedValues[fieldName];
  const primaryPhone = typeof watchedValues.phone === "string" ? watchedValues.phone : "";

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please add a valid phone alias"}
    >
      <PhoneAliasesEditor
        contact={{
          id: "temp-id",
          phone: primaryPhone,
          phone_aliases: Array.isArray(currentValue) ? currentValue : [],
        }}
        onUpdate={(updatedContact) => {
          setValue(fieldName, updatedContact.phone_aliases || [], {
            shouldValidate: true,
          });
        }}
        showEditorDirectly={true}
        readOnlyMode={true}
      />
    </FieldWrapper>
  );
};

/**
 * Render a technical_identifiers field (company support-engineering data).
 *
 * Object-array analog of EmailAliasesFieldRenderer: the editor runs in
 * readOnlyMode (no DB write in create/edit form) and syncs the RHF value on
 * every change so the parent form persists it on submit.
 */
export const TechnicalIdentifiersFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({
  ctx,
}) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const currentValue = watchedValues[fieldName];

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage}
    >
      <TechnicalIdentifiersEditor
        company={{
          id: "temp-id",
          technical_identifiers: parseTechnicalIdentifiers(currentValue),
        }}
        onUpdate={(next) => {
          setValue(fieldName, next, { shouldValidate: true });
        }}
        showEditorDirectly={true}
        readOnlyMode={true}
      />
    </FieldWrapper>
  );
};

/**
 * Render a ticket_id field (special case).
 */
export const TicketIdFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const currentValue = watchedValues[fieldName];

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage}
    >
      <TicketSelectWithModal
        value={currentValue || ""}
        onValueChange={(val: string) => setValue(fieldName, val, { shouldValidate: true })}
        placeholder={field.placeholder || "Select ticket..."}
        fullWidth
        highZIndex
        expandInline={true}
      />
    </FieldWrapper>
  );
};

/**
 * Render a template-select field.
 */
export const TemplateSelectFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const templateConfig = field.templateSelectConfig;

  if (!templateConfig) {
    return (
      <div key={fieldName} className="space-y-2">
        <label htmlFor={fieldName}>
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </label>
        <p className="text-sm text-red-600">
          Template configuration is required for template-select field
        </p>
      </div>
    );
  }

  const currentTemplate = watchedValues[fieldName];

  const handleTemplateSelect = useCallback(
    (template: BaseTemplate | null) => {
      // Set the template value and mapped fields without validating yet — validation runs
      // on submit. Validating here would run while other required fields are still empty
      // and throw ZodError (e.g. title/description required).
      setValue(fieldName, template, { shouldValidate: false });

      // Auto-populate form fields based on template and field mappings
      if (template) {
        const mappings = templateConfig.fieldMappings || {};
        const formValues = extractTemplateFormValues(template);

        if (Object.keys(mappings).length > 0) {
          Object.entries(mappings).forEach(([templateField, formField]) => {
            const value = formValues[templateField];
            if (value !== undefined && value !== null) {
              setValue(formField, value, { shouldValidate: false });
            }
          });
        } else {
          Object.entries(formValues).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              setValue(key, value, { shouldValidate: false });
            }
          });
        }
      }
    },
    [setValue, fieldName, templateConfig.fieldMappings]
  );

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage}
    >
      <ReusableTemplateSelector
        templateType={templateConfig.templateType}
        customTemplates={templateConfig.customTemplates}
        fieldMappings={templateConfig.fieldMappings}
        showClearOption={templateConfig.showClearOption}
        placeholder={templateConfig.placeholder || field.placeholder}
        onTemplateSelect={handleTemplateSelect}
        selectedTemplate={currentTemplate as BaseTemplate | null}
        className={hasError ? "border-red-500" : ""}
        expandInline={true}
      />
    </FieldWrapper>
  );
};
